// Lecture de CSV — portage de poc/src/csv.js.
//
// Le parseur encaisse ce qui sort réellement d'un tableur : séparateur détecté
// (pas supposé), guillemets RFC 4180, retours à la ligne dans un champ cité,
// CRLF comme LF, marque d'ordre des octets, lignes vides de fin de fichier.

export interface TableCsv {
  colonnes: string[];
  /** Une ligne par enregistrement, indexée par nom de colonne. */
  lignes: Record<string, string>[];
  separateur: string;
}

// « ; » d'abord : c'est ce que produit un tableur en locale française, et ce que
// produit le pipeline du POC.
const SEPARATEURS = [";", ",", "\t", "|"] as const;

function compterHorsGuillemets(ligne: string, separateur: string): number {
  let n = 0;
  let dedans = false;
  for (const c of ligne) {
    if (c === '"') dedans = !dedans;
    else if (!dedans && c === separateur) n++;
  }
  return n;
}

/** Le séparateur est celui qui découpe le plus la ligne d'en-tête ; à égalité, l'ordre tranche. */
export function detecterSeparateur(texte: string): string {
  const entete = texte.split(/\r?\n/, 1)[0] ?? "";
  let meilleur: string = SEPARATEURS[0];
  let score = -1;
  for (const sep of SEPARATEURS) {
    const n = compterHorsGuillemets(entete, sep);
    if (n > score) {
      score = n;
      meilleur = sep;
    }
  }
  return meilleur;
}

function decouper(texte: string, separateur: string): string[][] {
  const lignes: string[][] = [];
  let ligne: string[] = [];
  let champ = "";
  let dedans = false;
  for (let i = 0; i < texte.length; i++) {
    const c = texte[i];
    if (dedans) {
      if (c === '"') {
        if (texte[i + 1] === '"') {
          champ += '"';
          i++;
        } else dedans = false;
      } else champ += c;
    } else if (c === '"') {
      dedans = true;
    } else if (c === separateur) {
      ligne.push(champ);
      champ = "";
    } else if (c === "\n") {
      ligne.push(champ);
      lignes.push(ligne);
      ligne = [];
      champ = "";
    } else if (c !== "\r") {
      champ += c;
    }
  }
  if (champ !== "" || ligne.length) {
    ligne.push(champ);
    lignes.push(ligne);
  }
  return lignes;
}

const estVide = (ligne: string[]): boolean => ligne.every((v) => v.trim() === "");

/**
 * Analyse un CSV. Les lignes entièrement vides sont ignorées : un tableur en laisse
 * souvent traîner, et elles produiraient des projets fantômes.
 */
export function analyserCsv(texte: string, separateur?: string): TableCsv {
  const propre = texte.replace(/^\uFEFF/, "");
  const sep = separateur ?? detecterSeparateur(propre);
  const brut = decouper(propre, sep).filter((l) => !estVide(l));
  const [entete, ...corps] = brut;
  if (!entete) return { colonnes: [], lignes: [], separateur: sep };

  const colonnes = entete.map((c) => c.trim());
  const lignes = corps.map((cellules) => {
    const objet: Record<string, string> = {};
    colonnes.forEach((col, i) => {
      objet[col] = (cellules[i] ?? "").trim();
    });
    return objet;
  });
  return { colonnes, lignes, separateur: sep };
}
